/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import { useContext, useMemo, useState } from 'react';
import { loadSurvey, reloadSurveyListSummary } from '../../../actions/survey';
import { saveUniversalTrackerValue, UpdateActions } from '../../../actions/universalTrackerValue';
import { TOOLTIP } from '../../../constants/labels';
import { NotApplicableTypes, UtrvStatus } from '../../../constants/status';
import { useAppDispatch, useAppSelector } from '../../../reducers';
import { getCurrentUser } from '../../../selectors/user';
import { AnalyticsEvents } from '../../../services/analytics/AnalyticsEvents';
import { getAnalytics } from '../../../services/analytics/AnalyticsService';
import G17Client, { FlagProperties } from '../../../services/G17Client';
import { SurveyPermissions } from '../../../services/permissions/SurveyPermissions';
import { addSiteAlert, SiteAlertColors } from '../../../slice/siteAlertsSlice';
import { SubmitQuestionData } from '../../../utils/files';
import DelegationModal from '@features/delegation-modal';
import { BlockingLoader } from '@g17eco/atoms/loader';
import { useToggle } from '../../../hooks/useToggle';
import { BulkButton, countQuestions } from './ToggledButton';
import { ActionsMenu } from './ActionsMenu';
import './BulkActionToolbar.scss';
import { FeaturePermissions } from '../../../services/permissions/FeaturePermissions';
import { BulkActionUtrMin } from '../../../types/universalTracker';
import { useToggleCompleteSurveys } from '../../../hooks/useToggleCompleteSurveys';
import { QuestionConfigurationDropdown, QuestionConfigurationModal } from '@features/question-configuration';
import { QUESTION } from '@constants/terminology';
import { InitiativeUniversalTracker } from '@g17eco/types/initiativeUniversalTracker';
import { FloatingToolbar } from '@g17eco/molecules/floating-toolbar';
import { QuestionManagementContext } from '@features/question-management/QuestionManagementContainer';
import { SurveyFloatingToolbarButton } from '@utils/survey';
import { useChatbotVisibilityControl } from '@hooks/useChatbotVisibilityControl';
import { getNotReportingNote, NotReportingModal, omissionMap } from '@features/survey/question/NotReportingModal';
import { Option } from '@g17eco/molecules/select/SelectFactory';
import { isNa, isNr } from '@utils/universalTrackerValue';
import { getRootConfig } from '@selectors/globalData';
import { UniversalTrackerValuePlain } from '../../../types/universalTrackerValue';
import { SurveyActionData } from '@g17eco/types/surveyAction';
import { OmissionReason } from '@features/survey/question/constants';
import { ConfigurationType, InputOverrideType } from '../../../features/question-configuration/types';
import { useToggleCongratulationsModal } from '@features/survey/complete-survey-modal/useToggleCongratulationsModal';
import { CongratulationsModal } from '@features/survey/complete-survey-modal/CongratulationsModal';
import { BulkVerifyButton } from './BulkVerifyButton';

export type BulkActionToolbarSelectedUtrv = Pick<
  UniversalTrackerValuePlain,
  | '_id'
  | 'evidenceRequired'
  | 'verificationRequired'
  | 'noteRequired'
  | 'isPrivate'
  | 'status'
  | 'assuranceStatus'
  | 'stakeholders'
  | 'initiativeId'
  | 'universalTrackerId'
  | 'value'
  | 'valueData'
> & { utr?: BulkActionUtrMin };

export interface BulkActionToolbarProps {
  surveyId: string;
  initiativeId: string;
  selectedQuestions: BulkActionToolbarSelectedUtrv[];
  triggerBulkAction: (properties: FlagProperties | { status: UtrvStatus }) => void;
  toggleSelectAll: () => void;
  handleClose: () => void;
  questionCount: number;
  handleReload?: () => void;
  hiddenOptions?: (ConfigurationType | SurveyFloatingToolbarButton)[];
  surveyData: Pick<
    SurveyActionData,
    | '_id'
    | 'roles'
    | 'stakeholders'
    | 'initiativeId'
    | 'fragmentUniversalTrackerValues'
    | 'verificationRequired'
    | 'bulkVerifyEnabled'
    | 'workgroups'
  >;
  rootInitiativeUtrMap?: Map<string, InitiativeUniversalTracker>;
}

export const BulkActionToolbar = (props: BulkActionToolbarProps) => {
  const {
    selectedQuestions,
    handleClose,
    surveyId,
    surveyData,
    initiativeId,
    hiddenOptions = [],
    rootInitiativeUtrMap,
  } = props;
  const dispatch = useAppDispatch();
  const rootConfig = useAppSelector(getRootConfig);
  const canAccessVerification = FeaturePermissions.canAccessVerification(rootConfig);

  const [configType, setConfigType] = useState<ConfigurationType | undefined>();
  const [openDelegationModal, toggleOpenDelegationModal] = useToggle(false);
  const [openActionsMenu, toggleActionsMenu, setOpenActionsMenu] = useToggle(false);
  const [isLoading, setLoading] = useState(false);
  const currentUser = useAppSelector(getCurrentUser);
  const { setInputOverrideType, setMetricOverrideCode } = useContext(QuestionManagementContext);

  const [openNotReportingModal, toggleNotReportingModal, setNotReportingModal] = useToggle(false);
  const [comments, setComments] = useState<string>();

  const canContribute = currentUser && SurveyPermissions.canContribute(surveyData, currentUser);

  const selectedCount = useMemo(() => countQuestions(selectedQuestions), [selectedQuestions]);

  useChatbotVisibilityControl({ isHidden: selectedCount > 0 });

  const convertedSelectedQuestions = useMemo(
    () =>
      selectedQuestions.map((utrv) => {
        const { universalTrackerId, utr = {} } = utrv;
        const { name, valueType, valueValidation, unit, unitType, numberScale } = utr;
        return { _id: universalTrackerId, name, valueType, valueValidation, unit, unitType, numberScale };
      }),
    [selectedQuestions]
  );

  const handleReload = async () => {
    return Promise.all([
      dispatch(loadSurvey(surveyId, false, true)),
      dispatch(reloadSurveyListSummary()),
    ])
      .then(() => props.handleReload?.());
  }

  const { toggleCompleteSurveys } = useToggleCompleteSurveys({
    handleReload,
    surveys: [surveyData],
    isCompleting: true,
  });

  const { isCongratulationsModalOpen, openCongratulationsModal, toggleCongratulationsModal } =
    useToggleCongratulationsModal({ survey: surveyData, currentUser, toggleCompleteSurveys });

  if (!currentUser) {
    return null;
  }

  const handleCatch = () => {
    setLoading(false);
    dispatch(addSiteAlert({
      content: `Could not update ${QUESTION.PLURAL}`,
      color: SiteAlertColors.Danger
    }));
  }

  const reloadOnClosingModal = async ({ reloadSurvey = false }: { reloadSurvey?: boolean } = {}) => {
    if (!reloadSurvey) {
      return;
    }
    setLoading(true);
    await handleReload();
    setLoading(false);
  };

  function handleBulkFlagChange(properties: FlagProperties, utrvIds: string[]) {
    if (!canManage || !surveyId) {
      return;
    }
    setLoading(true);
    return G17Client.updateSurveyQuestionFlags({ surveyId, utrvIds, properties })
      .then(() => {
        getAnalytics().track(AnalyticsEvents.SurveyBulkAction, {
          utrvIds,
          properties,
          surveyId,
          initiativeId
        });
        props.triggerBulkAction(properties);
        return handleReload();
      })
      .then(() => {
        setLoading(false);
        setOpenActionsMenu(false);
      })
      .catch(handleCatch)
  }

  const handleBulkNAByType = (naType: string, omissionReason: OmissionReason) => {
    if (!canContribute) {
      return;
    }
    const handleSubmitQuestion = (utrvId: string, notApplicableType: string) => {
      const data: SubmitQuestionData = {
        autoVerify: 'true',
        valueData: { notApplicableType, input: {} },
        note: getNotReportingNote(omissionReason, comments),
      };
      return dispatch(saveUniversalTrackerValue(UpdateActions.STATUS.UPDATE, utrvId, data)) as unknown as Promise<unknown>;
    }

    setLoading(true);
    setNotReportingModal(false);
    Promise.all(selectedQuestions.map(selectedQuestion => handleSubmitQuestion(selectedQuestion._id, naType)))
      .then(() => handleReload())
      .then(() => {
        openCongratulationsModal({ editingUtrvIds: selectedQuestions.map(({_id}) => _id), verified: true })
      })
      .then(() => setLoading(false))
      .catch(handleCatch)
  }

  function downloadCurrentQuestionCsv() {
    if (surveyId) {
      const ids = selectedQuestions.map(u => u._id)
      return G17Client.downloadSurveyQuestions(surveyId, ids);
    }
  }

  const canManage = surveyData && SurveyPermissions.canManage(surveyData, currentUser);

  const toggledButtonCommon = {
    rootInitiativeUtrMap,
    selectedQuestions,
    handleBulkFlagChange,
    canManage,
  }

  const modalProps = {
    handleReload: reloadOnClosingModal,
    selectedQuestions: convertedSelectedQuestions,
    initiativeId,
  };

  const notReportingButton = !hiddenOptions.includes(SurveyFloatingToolbarButton.NotReporting) && canContribute ? (
    <BulkButton className='' tooltip={TOOLTIP.notReporting} onClick={toggleNotReportingModal}>
      Not reporting
    </BulkButton>
  ) : null;

  const downloadButton = (
    <BulkButton className='d-flex gap-2'
      tooltip='Download'
      onClick={downloadCurrentQuestionCsv}
    >
      <i className='fal fa-file-csv text-ThemeIconSecondary' />
      Download
    </BulkButton>
  );

  const delegateButton = canManage ? (
    <BulkButton className='d-flex gap-2' tooltip='Delegate' onClick={toggleOpenDelegationModal}>
      <i className='fal fa-users-medical text-ThemeIconSecondary' />
      Delegate
    </BulkButton>
  ) : null;

  const reportSetting = canManage ? (
    <ActionsMenu
      {...toggledButtonCommon}
      openMenu={openActionsMenu}
      toggleMenu={toggleActionsMenu}
      canAccessVerification={canAccessVerification}
    />
  ) : null

  const allConfigurationTypeAreHidden = Object.values(ConfigurationType).every((option) =>
    hiddenOptions.includes(option)
  );

  const isMultipleQuestionSelected = modalProps.selectedQuestions.length > 1

  const platformMetricOverridesButton =
    hiddenOptions.includes(SurveyFloatingToolbarButton.Platform) || allConfigurationTypeAreHidden ? null : (
      <>
        <QuestionConfigurationDropdown
          changeConfigType={(configType) => {
            setConfigType(configType);
            setInputOverrideType(isMultipleQuestionSelected ? InputOverrideType.NumberScale : undefined);
            setMetricOverrideCode(isMultipleQuestionSelected ? 'verificationRequired' : undefined);
          }}
          dropdownToggle={
            <div>
              <i className='fa-light fa-square-question fs-5 me-2'></i> Platform {QUESTION.SINGULAR} overrides
            </div>
          }
          hiddenOptions={
            hiddenOptions.filter((op: any) => Object.values(ConfigurationType).includes(op)) as ConfigurationType[]
          }
          {...modalProps}
        />
        {configType ? (
          <QuestionConfigurationModal
            isOpen={true}
            toggleOpen={() => {
              setConfigType(undefined);
            }}
            configType={configType}
            {...modalProps}
          />
        ) : null}
      </>
    );

  const selectedCountLabel = (
    <span className='p-1 text-colorThemeTextMedium d-inline-block' style={{ minWidth: '80px' }}>
      {selectedCount} selected
    </span>
  );

  const isAllNaQuestions = selectedQuestions.every((question) => isNa(question));
  const isAllNrQuestions = selectedQuestions.every((question) => isNr(question));
  const omissionOptions: Option<OmissionReason>[] = [
    {
      label: omissionMap.na.label,
      value: omissionMap.na.value,
      isDisabled: isAllNaQuestions,
    },
    ...[omissionMap.nrProhibitions, omissionMap.nrConstraints, omissionMap.nrUnavailable].map(({ label, value }) => ({
      label,
      value,
      isDisabled: isAllNrQuestions,
    })),
  ];

  const verifyMetricsButton = surveyData.bulkVerifyEnabled ? (
    <BulkVerifyButton
      selectedQuestions={selectedQuestions}
      survey={surveyData}
      user={currentUser}
      handleReload={() => {
        props.triggerBulkAction({ status: UtrvStatus.Verified });
        return handleReload();
      }}
    />
  ) : null;

  return (
    <>
      {isLoading ? <BlockingLoader /> : null}
      <FloatingToolbar
        handleClose={handleClose}
        items={[
          notReportingButton,
          verifyMetricsButton,
          reportSetting,
          platformMetricOverridesButton,
          delegateButton,
          downloadButton,
          selectedCountLabel,
        ]}
        isOpen={selectedCount > 0}
      />

      <DelegationModal
        isOpen={openDelegationModal}
        survey={surveyData}
        handleSubmit={async () => {
          setLoading(true);
          await handleReload();
          setLoading(false);
        }}
        toggle={toggleOpenDelegationModal}
        initiativeId={initiativeId}
        utrvs={surveyData.fragmentUniversalTrackerValues.filter((q) => selectedQuestions.some((u) => u._id === q._id))}
      />
      <CongratulationsModal isOpen={isCongratulationsModalOpen} toggle={toggleCongratulationsModal} />
      <NotReportingModal
        key={openNotReportingModal ? 'open-not-reporting' : 'close-not-reporting'}
        isOpen={openNotReportingModal}
        toggle={() => setNotReportingModal(false)}
        options={omissionOptions}
        comments={comments}
        submitNA={(reason) => handleBulkNAByType(NotApplicableTypes.na, reason)}
        submitNR={(reason) => handleBulkNAByType(NotApplicableTypes.nr, reason)}
        handleComments={(value) => setComments(value)}
      />
    </>
  );
};
